import React, { useState, useRef, useMemo } from "react";

/**
 * DxfViewer — SVG-based 2D viewer for normalized entities in mm.
 * Rendering is purely presentational: metrics come from the backend.
 *
 * props:
 *  - entities: NormalizedEntity[] from GET /api/cad-jobs/:id/entities
 *  - bbox: {x, y, z} width/height (mm)
 *  - anomalies: [{code, severity, detail}]
 *  - layers: [{name, color, on, frozen}]
 *  - selectedAnomalyIdx: index of anomaly to highlight (optional)
 */
export default function DxfViewer({ entities = [], bbox, anomalies = [], layers = [] }) {
  const containerRef = useRef(null);
  const [layersOn, setLayersOn] = useState(() =>
    Object.fromEntries((layers || []).map(l => [l.name, !l.frozen]))
  );
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  // Compute effective bbox from entities if not provided
  const computedBbox = useMemo(() => {
    if (bbox && bbox.x && bbox.y) return { width: bbox.x, height: bbox.y, xmin: 0, ymin: 0 };
    if (!entities.length) return { width: 100, height: 100, xmin: 0, ymin: 0 };
    let xs = [], ys = [];
    entities.forEach(e => {
      const p = e.payload || {};
      if (e.type === "LINE") { xs.push(p.a[0], p.b[0]); ys.push(p.a[1], p.b[1]); }
      else if (e.type === "CIRCLE" || e.type === "ARC") {
        xs.push(p.c[0] - p.r, p.c[0] + p.r); ys.push(p.c[1] - p.r, p.c[1] + p.r);
      } else if (e.type === "LWPOLYLINE" || e.type === "SPLINE") {
        (p.points || []).forEach(pt => { xs.push(pt[0]); ys.push(pt[1]); });
      } else if (e.type === "ELLIPSE") {
        const r = Math.hypot(p.major[0], p.major[1]);
        xs.push(p.c[0] - r, p.c[0] + r); ys.push(p.c[1] - r, p.c[1] + r);
      }
    });
    const xmin = Math.min(...xs), xmax = Math.max(...xs);
    const ymin = Math.min(...ys), ymax = Math.max(...ys);
    return { width: Math.max(1, xmax - xmin), height: Math.max(1, ymax - ymin), xmin, ymin };
  }, [entities, bbox]);

  const padding = 10;
  const viewW = computedBbox.width + padding * 2;
  const viewH = computedBbox.height + padding * 2;

  // Color mapping: closed = solid, open = warning color
  const strokeFor = (e) => {
    if (e.is_closed) return "#0F172A"; // outer/inner closed = dark
    return "#D97706"; // open = yellow
  };

  const renderEntity = (e, idx) => {
    if (!layersOn[e.layer] && layersOn[e.layer] !== undefined) return null;
    const p = e.payload || {};
    const sw = 0.35 / zoom;
    const stroke = strokeFor(e);
    if (e.type === "LINE") {
      return <line key={idx} x1={p.a[0]} y1={-p.a[1]} x2={p.b[0]} y2={-p.b[1]}
                   stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />;
    }
    if (e.type === "CIRCLE") {
      return <circle key={idx} cx={p.c[0]} cy={-p.c[1]} r={p.r}
                     fill="none" stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />;
    }
    if (e.type === "ARC") {
      const a1 = (p.start_angle * Math.PI) / 180;
      const a2 = (p.end_angle * Math.PI) / 180;
      const x1 = p.c[0] + p.r * Math.cos(a1);
      const y1 = -(p.c[1] + p.r * Math.sin(a1));
      const x2 = p.c[0] + p.r * Math.cos(a2);
      const y2 = -(p.c[1] + p.r * Math.sin(a2));
      const sweep = (p.end_angle - p.start_angle + 360) % 360;
      const largeArc = sweep > 180 ? 1 : 0;
      return <path key={idx} d={`M ${x1} ${y1} A ${p.r} ${p.r} 0 ${largeArc} 0 ${x2} ${y2}`}
                   fill="none" stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />;
    }
    if (e.type === "LWPOLYLINE" || e.type === "SPLINE") {
      const pts = (p.points || []).map(pt => `${pt[0]},${-pt[1]}`).join(" ");
      const El = e.is_closed ? "polygon" : "polyline";
      return React.createElement(El, {
        key: idx, points: pts, fill: "none", stroke, strokeWidth: sw, vectorEffect: "non-scaling-stroke"
      });
    }
    if (e.type === "ELLIPSE") {
      const rx = Math.hypot(p.major[0], p.major[1]);
      const ry = rx * p.ratio;
      return <ellipse key={idx} cx={p.c[0]} cy={-p.c[1]} rx={rx} ry={ry}
                      fill="none" stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />;
    }
    return null;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(z => Math.max(0.1, Math.min(z * delta, 40)));
  };
  const onDown = (e) => { setDragging(true); setDragStart({ x: e.clientX, y: e.clientY, px: pan.x, py: pan.y }); };
  const onMove = (e) => {
    if (!dragging) return;
    const dx = (e.clientX - dragStart.x) / zoom;
    const dy = (e.clientY - dragStart.y) / zoom;
    setPan({ x: dragStart.px + dx, y: dragStart.py + dy });
  };
  const onUp = () => setDragging(false);
  const fit = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  return (
    <div className="border border-border bg-white" data-testid="dxf-viewer">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border text-xs">
        <div className="flex items-center gap-3">
          <button onClick={fit} className="px-2 py-0.5 border border-border hover:bg-muted" data-testid="viewer-fit">Fit</button>
          <button onClick={() => setZoom(z => z * 1.25)} className="px-2 py-0.5 border border-border hover:bg-muted" data-testid="viewer-zoom-in">+</button>
          <button onClick={() => setZoom(z => z / 1.25)} className="px-2 py-0.5 border border-border hover:bg-muted" data-testid="viewer-zoom-out">−</button>
          <span className="font-mono text-muted-foreground">zoom {zoom.toFixed(2)}×</span>
          <span className="font-mono text-muted-foreground">bbox {computedBbox.width.toFixed(1)} × {computedBbox.height.toFixed(1)} mm</span>
        </div>
        <div className="flex items-center gap-2">
          {(layers || []).map(l => (
            <label key={l.name} className="flex items-center gap-1 text-[10px] uppercase tracking-widest">
              <input type="checkbox" checked={!!layersOn[l.name]}
                     onChange={(e) => setLayersOn(s => ({ ...s, [l.name]: e.target.checked }))}
                     data-testid={`layer-toggle-${l.name}`} />
              {l.name}
            </label>
          ))}
        </div>
      </div>
      <div
        ref={containerRef}
        className="relative"
        style={{ height: 480, background: "repeating-linear-gradient(0deg,#F8FAFC,#F8FAFC 19px,#E2E8F0 20px), repeating-linear-gradient(90deg,#F8FAFC,#F8FAFC 19px,#E2E8F0 20px)", cursor: dragging ? "grabbing" : "grab" }}
        onWheel={onWheel} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
      >
        <svg width="100%" height="100%"
             viewBox={`${computedBbox.xmin - padding} ${-(computedBbox.ymin + computedBbox.height + padding)} ${viewW} ${viewH}`}
             preserveAspectRatio="xMidYMid meet">
          <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
            {entities.map((e, i) => renderEntity(e, i))}
          </g>
        </svg>
      </div>
      {(anomalies || []).length > 0 && (
        <div className="border-t border-border px-3 py-2 text-xs">
          <div className="font-display font-bold mb-1">Anomalie rilevate</div>
          <ul className="space-y-1">
            {anomalies.map((a, i) => (
              <li key={i} className={`px-2 py-1 border ${a.severity === "critical" ? "bg-reliability-red" : a.severity === "warning" ? "bg-reliability-yellow" : "bg-muted"}`}>
                <span className="uppercase text-[10px] tracking-widest mr-2">{a.code}</span>
                {a.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
